import type { LiveGameStatusUI, LocalStateUI } from "../../../../common/types.ts";

type ScoreStripGame = LocalStateUI["games"][number];

export type LeagueTopBarGameState = {
	awayPts?: number;
	homePts?: number;
	kind: "final" | "halftime" | "live" | "upcoming";
	numPeriods?: number;
	period?: number;
	target?: number;
	time?: string;
};

export const createLiveGameStatus = (boxScore: any): LiveGameStatusUI | undefined => {
	if (typeof boxScore?.gid !== "number" || boxScore.gid < 0 || boxScore.gameOver) {
		return undefined;
	}

	const numPeriods = boxScore.numPeriods ?? 4;
	const period = Math.max(
		1,
		...(boxScore.teams ?? []).map((team: any) => team.ptsQtrs?.length ?? 0),
	);
	const teams = (boxScore.teams ?? []).slice(0, 2).map((team: any) => ({
		pts: Number(team.pts ?? 0),
		tid: Number(team.tid),
	}));
	if (teams.length !== 2 || teams.some((team: { tid: number }) => !Number.isFinite(team.tid))) {
		return undefined;
	}

	return {
		gid: boxScore.gid,
		halftime: boxScore.time === "0:00" && period === Math.ceil(numPeriods / 2),
		numPeriods,
		period,
		...(typeof boxScore.elamTarget === "number" ? { target: boxScore.elamTarget } : {}),
		teams: teams as LiveGameStatusUI["teams"],
		...(typeof boxScore.time === "string" ? { time: boxScore.time } : {}),
	};
};

export const getLeagueTopBarGameState = ({
	game,
	liveGameInProgress,
	liveGameStatus,
}: {
	game: ScoreStripGame;
	liveGameInProgress: boolean;
	liveGameStatus?: LiveGameStatusUI;
}): LeagueTopBarGameState => {
	if (liveGameInProgress) {
		const status = liveGameStatus?.gid === game.gid ? liveGameStatus : undefined;
		const away = status?.teams.find((team) => team.tid === game.teams[0].tid);
		const home = status?.teams.find((team) => team.tid === game.teams[1].tid);

		return {
			awayPts: away?.pts,
			homePts: home?.pts,
			kind: status?.halftime ? "halftime" : "live",
			numPeriods: status?.numPeriods,
			period: status?.period,
			target: status?.target,
			time: status?.time,
		};
	}

	if (game.teams[0].pts !== undefined && game.teams[1].pts !== undefined) {
		return {
			awayPts: game.teams[0].pts,
			homePts: game.teams[1].pts,
			kind: "final",
		};
	}

	return { kind: "upcoming" };
};
